import ax from "axios";
import { toast } from "react-toastify";
import endpoint from "./endpoints";

// Deliberately not a hardcoded prod domain: fall back to localhost so a
// misconfigured build fails loudly in dev instead of silently talking to production.
const axios = ax.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000/api",
  timeout: 25000,
});

axios.interceptors.request.use(
  (config) => {
    config.headers["ngrok-skip-browser-warning"] = "true";
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // 1. Silent handling for intentional request cancellations / aborted navigation
    if (
      ax.isCancel(error) ||
      error.name === "CanceledError" ||
      error.code === "ERR_CANCELED"
    ) {
      error._handled = true;
      return Promise.reject(error);
    }

    const config = error.config;

    // 2. Safe single auto-retry for idempotent GET requests on network/transient blips
    if (
      config &&
      (!config.method || config.method.toLowerCase() === "get") &&
      !config._retry
    ) {
      const isNetworkOrServerTransient =
        !error.response ||
        error.response.status === 502 ||
        error.response.status === 503 ||
        error.response.status === 504 ||
        error.code === "ECONNABORTED" ||
        error.code === "ERR_NETWORK";

      if (isNetworkOrServerTransient) {
        config._retry = true;
        // Wait 350ms before retrying once
        await new Promise((resolve) => setTimeout(resolve, 350));
        return axios(config);
      }
    }

    // 3. Unauthorized (401)
    if (error.response && error.response.status === 401) {
      const requestUrl = error.config?.url || "";
      const isAuthEndpoint = requestUrl.includes(endpoint.LOGIN);
      const hadToken = !!localStorage.getItem("token");

      if (hadToken && !isAuthEndpoint) {
        localStorage.removeItem("token");
        window.location.href = "/login";
      }
      error._handled = true;
      return Promise.reject(error);
    }

    // 4. Subscription Expired (403)
    if (
      error.response &&
      error.response.status === 403 &&
      error.response.data?.code === "SUBSCRIPTION_EXPIRED"
    ) {
      toast.error(
        "Your subscription has expired — click to renew your plan",
        {
          toastId: "subscription_expired",
          onClick: () => {
            window.location.href = "/billing";
          },
          autoClose: 8000,
        },
      );
      error._handled = true;
      return Promise.reject(error);
    }

    // 5. Rate Limit / Quota Exceeded (429)
    if (error.response && error.response.status === 429) {
      const msg = error.response.data?.message || "";
      const isRateLimit = /too many attempts|too many requests/i.test(msg);

      if (isRateLimit) {
        toast.warn("Too many requests — please slow down and wait a moment", {
          toastId: "rate_limit_429",
          autoClose: 4000,
        });
      } else {
        toast.error(
          `${msg || "Message quota exceeded"} — click to renew your plan`,
          {
            toastId: "quota_exceeded",
            onClick: () => {
              window.location.href = "/billing";
            },
            autoClose: 6000,
          },
        );
      }
      error._handled = true;
      return Promise.reject(error);
    }

    // 6. WhatsApp Account Unusable (422)
    if (
      error.response &&
      error.response.status === 422 &&
      typeof error.response.data?.message === "string" &&
      /not active\/usable/i.test(error.response.data.message)
    ) {
      toast.error(
        "Your WhatsApp account isn't active — click to check its status",
        {
          toastId: "whatsapp_account_unusable",
          onClick: () => {
            window.location.href = "/account";
          },
          autoClose: 8000,
        },
      );
      error._handled = true;
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export default axios;
